import {
    ChonkyActions,
    ChonkyFileActionData,
    ChonkyIconName,
    defineFileAction,
    FileArray,
    FileBrowser,
    FileBrowserHandle,
    FileContextMenu,
    FileData,
    FileHelper,
    FileList,
    FileNavbar,
    FileToolbar,
    I18nConfig
} from '@aperturerobotics/chonky';

import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    Cloud,
    EditOutlined,
    Info,
    LibraryBooksOutlined,
    LockOutlined,
    Public,
    QuestionMarkOutlined,
    Search,
    SettingsOutlined,
    ShieldOutlined
} from '@mui/icons-material';
import {
    Autocomplete,
    Backdrop,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    DialogActions,
    Divider,
    FormControl,
    FormControlLabel,
    Grid2 as Grid,
    InputAdornment,
    InputLabel,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Modal,
    Paper,
    Select,
    Skeleton,
    Stack,
    SvgIconTypeMap,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { downloadUrl, formatBytes } from 'helpers/files.helper';
import { capitalizeFirstLetter, loadCheck } from 'helpers/global.helper';
import { isUserSuperAdmin } from 'helpers/users.helper';
import useAppSelector from 'hooks/useAppSelector';
import useAuth from 'hooks/useAuth';
import useLightbox from 'hooks/useLightbox';
import { atom, useAtom } from 'jotai';
import { md5 } from 'js-md5';
import _ from 'lodash';
import moment from 'moment';
import React, { Dispatch, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import slugify from 'react-slugify';
import { toast } from 'react-toastify';
import {
    useCompressDriveItemsMutation,
    useCreateDriveFolderMutation,
    useDeleteDriveItemsMutation,
    useManageDriveItemsMutation,
    useMoveDriveItemsMutation,
    useRenameDriveItemsMutation,
    useUploadDriveFileMutation
} from 'store/api/drive.api';
import { useFetchUsersQuery } from 'store/api/user.api';
import Swal from 'sweetalert2';
import { Nullable, TLike } from 'types/global.types';
import { RoleId } from 'types/user.types';
import ModalFormCard from 'ui-component/cards/ModalCard';
import { z } from 'zod';

export const drivePathAtom = atom<string | null>(null);

drivePathAtom.onMount = (set) => {
    const unMount = () => {
        set(null);
    };
    return unMount;
};

export interface DriveFiles {
    [fileId: string]: CustomFileData;
}

export interface DatabaseFileData {
    isSystem: boolean;
    isPublic: boolean;
    ownerId: number;
    ownerName: string;
    filePath: string;

    readPermissions: number[];
    editPermissions: number[];
}

// We define a custom interface for file data because we want to add some custom fields
// to Chonky's built-in `FileData` interface.
export interface CustomFileData extends FileData {
    parentId?: string;
    childrenIds?: string[];

    url?: string;
    path?: string;
    createDate?: string;

    isSystem?: boolean | null;
    isPublic?: boolean | null;
    ownerId?: number | null;
    ownerName?: string | null;
    filePath?: string | null;

    readPermissions?: number[];
    editPermissions?: number[];
}
interface CustomFileMap {
    [fileId: string]: CustomFileData;
}

interface Permission {
    id: string;
    name: string;
    icon: OverridableComponent<SvgIconTypeMap<object, 'svg'>>;
    darker?: boolean;
}

const permissions = {
    read: { id: 'read', name: 'Read', icon: LibraryBooksOutlined, darker: true },
    edit: { id: 'edit', name: 'Edit', icon: EditOutlined },
    fullAccessSuperAdmin: { id: 'full_access_super_admin', name: 'Full Access (Super Admin)', icon: ShieldOutlined },
    fullAccessOwner: { id: 'full_access_owner', name: 'Full Access (Owner)', icon: ShieldOutlined },
    none: { id: 'none', name: 'None', icon: QuestionMarkOutlined, darker: true }
};

const handleFilePermissions = (
    file: Pick<CustomFileData, 'isSystem' | 'isPublic' | 'ownerId' | 'readPermissions' | 'editPermissions'>,
    user: { id: number; role: RoleId }
): Permission[] => {
    const groupedPermissions = [];

    if (file.isSystem === true) return [permissions.read];
    else if (file.ownerId === user.id) return [permissions.fullAccessOwner];
    else if (isUserSuperAdmin(user!.role)) return [permissions.fullAccessSuperAdmin];

    if (file.isPublic || file.readPermissions?.includes(user.id)) groupedPermissions.push(permissions.read);

    if (file.editPermissions?.includes(user.id)) groupedPermissions.push(permissions.edit);

    if (groupedPermissions.length > 0) return groupedPermissions;
    else return [permissions.none];
};

const useFiles = (fileMap: CustomFileMap, currentFolderId: string | null): FileArray => {
    return useMemo(() => {
        if (_.isEmpty(fileMap) || !currentFolderId) return [];

        const currentFolder = fileMap[currentFolderId];
        const childrenIds = currentFolder.childrenIds!;
        const files = childrenIds ? childrenIds.map((fileId: string) => fileMap[fileId]) : [];
        return files;
    }, [currentFolderId, fileMap]);
};

const useFolderChain = (fileMap: CustomFileMap, currentFolderId: string | null): FileArray => {
    return useMemo(() => {
        if (_.isEmpty(fileMap) || !currentFolderId) return [];

        const currentFolder = fileMap[currentFolderId];

        const folderChain = [currentFolder];

        let parentId = currentFolder.parentId;
        while (parentId) {
            const parentFile = fileMap[parentId];
            if (parentFile) {
                folderChain.unshift(parentFile);
                parentId = parentFile.parentId;
            } else {
                break;
            }
        }

        return folderChain;
    }, [currentFolderId, fileMap]);
};

const generateId = (path: string) => {
    const formattedPath = path.trim().toLowerCase();
    if (formattedPath === 'drive') return 'root';
    else return md5(formattedPath);
};

const useCustomFileMap = (baseFileMap: DriveFiles | undefined) => {
    const [drivePath, setDrivePath] = useAtom(drivePathAtom);

    const rootFolderId = drivePath ? generateId(drivePath) : null;

    const { user } = useAuth();

    // Setup the React state for our file map and the current folder.
    const [fileMap, setFileMap] = useState(baseFileMap || {});
    const [currentFolderId, setCurrentFolderId] = useState(rootFolderId);

    const { t } = useTranslation();

    // @ts-ignore
    const getResultTranslation = (message: string): string => t(`pages.drive_page.${slugify(message, { delimiter: '_' })}`);

    useEffect(() => {
        setFileMap(baseFileMap || {});
    }, [baseFileMap]);

    const [deleteServerItems] = useDeleteDriveItemsMutation();
    const [createServerFolder] = useCreateDriveFolderMutation();
    const [uploadServerFile] = useUploadDriveFileMutation();
    const [moveServerItems] = useMoveDriveItemsMutation();
    const [renameServerItems] = useRenameDriveItemsMutation();
    const [compressServerItems] = useCompressDriveItemsMutation();

    // Setup logic to listen to changes in current folder ID without having to update
    // `useCallback` hooks. Read more about it here:
    // https://reactjs.org/docs/hooks-faq.html#is-there-something-like-instance-variables
    const currentFolderIdRef = useRef(currentFolderId);
    useEffect(() => {
        currentFolderIdRef.current = currentFolderId;
        if (baseFileMap && currentFolderId) setDrivePath(baseFileMap[currentFolderId].path!);
    }, [currentFolderId, baseFileMap]);

    const currentFolderPathRef = useRef(drivePath);
    useEffect(() => {
        currentFolderPathRef.current = drivePath;
    }, [drivePath]);

    // Function that will be called when user deletes files either using the toolbar
    // button or `Delete` key.
    const deleteFiles = useCallback((files: CustomFileData[]) => {
        const items = files.map((file) => file.path!);
        toast.promise(
            deleteServerItems({ items, userId: user!.id })
                .unwrap()
                .then((result) => {
                    if (result && 'message' in result) toast.success(getResultTranslation(result.message));
                })
                .catch((error) => {
                    console.log({ error });
                    if (error.data && 'message' in error.data) toast.error(getResultTranslation(error.data.message));
                }),
            { pending: t('notifications.pending.deletePending') }
        );
    }, []);

    // Function that will be called when files are moved from one folder to another
    // using drag & drop.
    const moveFiles = useCallback((files: CustomFileData[], destination: CustomFileData) => {
        toast.promise(
            moveServerItems({ destination: destination.path!, items: files.map((file) => file.path!), userId: user!.id })
                .unwrap()
                .then((result) => {
                    if (result && 'message' in result) toast.success(getResultTranslation(result.message));
                })
                .catch((error) => {
                    console.log({ error });
                    if (error.data && 'message' in error.data) toast.error(getResultTranslation(error.data.message));
                }),
            {
                pending: t('notifications.pending.movePending')
            }
        );
    }, []);

    const compressFiles = useCallback((files: CustomFileData[], fileName: string) => {
        if (!currentFolderPathRef.current) return;
        toast.promise(
            compressServerItems({
                items: files.map((file) => file.path!),
                userId: user!.id,
                fileName,
                destination: currentFolderPathRef.current
            })
                .unwrap()
                .then((result) => {
                    if (result && 'message' in result) toast.success(getResultTranslation(result.message));
                })
                .catch((error) => {
                    console.log({ error });
                    if (error.data && 'message' in error.data) toast.error(getResultTranslation(error.data.message));
                }),
            {
                pending: t('notifications.pending.createPending')
            }
        );
    }, []);

    const createFolder = useCallback(async (folderName: string) => {
        if (!currentFolderPathRef.current) return;
        // If creating folder in server fails for some reason display an error and then return
        toast.promise(
            createServerFolder({ name: folderName, path: currentFolderPathRef.current, userId: user!.id })
                .unwrap()
                .then((result) => {
                    if (result && 'message' in result) toast.success(getResultTranslation(result.message));
                })
                .catch((error) => {
                    console.log({ error });
                    if (error.data && 'message' in error.data) toast.error(getResultTranslation(error.data.message));
                }),
            { pending: t('notifications.pending.createPending') }
        );
    }, []);

    const uploadFiles = useCallback(async (files: File[]) => {
        if (!currentFolderPathRef.current) return;

        toast.promise(
            uploadServerFile({ files, path: currentFolderPathRef.current, userId: user!.id })
                .unwrap()
                .then((result) => {
                    if (result && 'message' in result) toast.success(getResultTranslation(result.message));
                })
                .catch((error) => {
                    console.log({ error });
                    if (error.status === 'FETCH_ERROR') toast.error(t('pages.drive_page.files_too_large', { gb: '5GB' }));
                    else if (error.data && 'message' in error.data) toast.error(getResultTranslation(error.data.message));
                }),
            {
                pending: t('notifications.pending.uploadPending')
            }
        );
    }, []);

    const renameFiles = useCallback(async (files: CustomFileData[], name: string) => {
        toast.promise(
            renameServerItems({ files: files.map((file) => file.path!), name, userId: user!.id })
                .unwrap()
                .then((result) => {
                    if (result && 'message' in result) toast.success(getResultTranslation(result.message));
                })
                .catch((error) => {
                    console.log({ error });
                    if (error.data && 'message' in error.data) toast.error(getResultTranslation(error.data.message));
                }),
            {
                pending: t('notifications.pending.uploadPending')
            }
        );
    }, []);

    return {
        fileMap,
        currentFolderId,
        setCurrentFolderId,
        deleteFiles,
        renameFiles,
        compressFiles,
        moveFiles,
        createFolder,
        uploadFiles
    };
};

export const useFileActionHandler = (
    setCurrentFolderId: (folderId: string) => void,
    deleteFiles: (files: CustomFileData[]) => void,
    compressFiles: (files: CustomFileData[], fileName: string) => void,
    moveFiles: (files: CustomFileData[], destination: FileData) => void,
    renameFiles: (files: CustomFileData[], name: string) => void,
    createFolder: (folderName: string) => void,
    uploadFiles: (files: File[]) => void,
    handleUploadClick: (type: 'files' | 'folder') => void,
    clearSelection: () => void,
    setSearchTerm: Dispatch<React.SetStateAction<string>>,
    files: CustomFileData[],
    CustomActions: any,
    pathingDisabled = false
) => {
    const { showExtended } = useLightbox();
    const { t } = useTranslation();

    const [_, setDrivePath] = useAtom(drivePathAtom);

    const navigate = useNavigate();

    return useCallback(
        (data: ChonkyFileActionData) => {
            // Handle directories
            if (data.id === ChonkyActions.OpenFiles.id) {
                const { targetFile, files } = data.payload;
                const fileToOpen: CustomFileData = targetFile ?? files[0];
                if (fileToOpen && FileHelper.isDirectory(fileToOpen)) {
                    if (!pathingDisabled) navigate(`/drive/${fileToOpen.id}`, { replace: true });
                    setCurrentFolderId(fileToOpen.id);
                    if (fileToOpen.path) setDrivePath(fileToOpen.path);
                    setSearchTerm('');
                    return;
                }
                // Handle default files
            } else if (
                data.id === ChonkyActions.MouseClickFile.id &&
                data.payload.clickType === 'double' &&
                !FileHelper.isDirectory(data.payload.file)
            ) {
                const file = data.payload.file as CustomFileData;
                switch (file.ext) {
                    case 'rar':
                    case '7z':
                    case 'tar':
                    case 'zip':
                        downloadUrl({ src: file.url!, name: file.name });
                        break;
                    default:
                        if (files) {
                            const filteredFiles = files.filter((file) => !file.isDir);
                            showExtended(
                                filteredFiles.map((file) => ({ url: file.url!, id: file.id })),
                                { initialIndex: filteredFiles.findIndex((arrayFile) => arrayFile.id == file.id) }
                            );
                        }
                        break;
                }
            } else if (data.id === ChonkyActions.UploadFiles.id) handleUploadClick('files');
            else if (data.id === CustomActions.UploadFolder.id) handleUploadClick('folder');
            else if (data.id === CustomActions.RenameFiles.id) {
                const file = data.state.selectedFilesForAction[0];
                let name = file.name;
                if (!file.isDir) name = name.slice(0, name.lastIndexOf('.'));

                NiceModal.show(inputDialog, {
                    submit: ({ name }) => {
                        if (name) renameFiles(data.state.selectedFilesForAction!, name);
                    },
                    name,
                    items: data.state.selectedFilesForAction,
                    title: 'pages.drive_page.rename',
                    additionalMessage: 'pages.drive_page.file_to_rename',
                    inputLabel: 'pages.drive_page.new_file_name'
                }).then(() => clearSelection());
            } else if (data.id === ChonkyActions.DeleteFiles.id) {
                Swal.fire({
                    title: t('pages.order_manage_page.order_products.you_are_deleting_are_you_sure', {
                        length: data.state.selectedFilesForAction!.length,
                        word: data.state.selectedFilesForAction!.length === 1 ? 'files' : 'files'
                    }),
                    text: t('notifications.swal.text.you_wont_be_able_to_revert'),
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: t('notifications.swal.confirm_button.yes_delete_it')
                }).then((result) => {
                    if (result.isConfirmed) deleteFiles(data.state.selectedFilesForAction!);
                });
            } else if (data.id === ChonkyActions.MoveFiles.id) {
                moveFiles(data.payload.files, data.payload.destination);
                clearSelection();
            } else if (data.id === ChonkyActions.CreateFolder.id) {
                NiceModal.show(inputDialog, {
                    submit: ({ name }) => {
                        if (name) createFolder(name);
                    },
                    title: 'pages.drive_page.new_folder',
                    inputLabel: 'pages.drive_page.folder_name'
                });
            } else if (data.id === CustomActions.Download.id) {
                const files: CustomFileData[] = data.state.selectedFiles;

                if (files.length === 0) toast.error('File not selected');
                else {
                    const filteredFiles = files.filter((file) => !file.isDir);
                    for (const file of filteredFiles) if (file.url && file.name) downloadUrl({ src: file.url, name: file.name });
                }
            } else if (data.id === CustomActions.Details.id) {
                const file: CustomFileData = data.state.selectedFiles[0];

                NiceModal.show(fileDetailsDialog, { file });
            } else if (data.id === CustomActions.Manage.id) {
                const file: CustomFileData = data.state.selectedFiles[0];

                NiceModal.show(fileManageDialog, { file });
            } else if (data.id === CustomActions.Compress.id) {
                const file = data.state.selectedFilesForAction[0];
                let name = file.name;
                if (!file.isDir) name = name.slice(0, name.lastIndexOf('.'));

                NiceModal.show(inputDialog, {
                    submit: ({ name }) => {
                        if (name) compressFiles(data.state.selectedFilesForAction!, name);
                    },
                    name,
                    items: data.state.selectedFilesForAction,
                    title: 'pages.drive_page.compress',
                    inputLabel: 'pages.drive_page.new_archive_name',
                    additionalMessage: 'pages.drive_page.files_to_compress'
                }).then(() => clearSelection());
            }
        },
        [createFolder, deleteFiles, moveFiles, setCurrentFolderId, uploadFiles, files]
    );
};

interface Props {
    baseFileMap?: DriveFiles;
    isBaseFileMapFetching: boolean;
    // Enable current folder mirroring into path & open files
    pathingDisabled?: boolean;
}

const Drive: React.FC<Props> = ({ baseFileMap, isBaseFileMapFetching, pathingDisabled = false }) => {
    const { user } = useAuth();

    const { fileMap, currentFolderId, setCurrentFolderId, deleteFiles, compressFiles, moveFiles, renameFiles, createFolder, uploadFiles } =
        useCustomFileMap(baseFileMap);

    const customFileFilter = (file: CustomFileData | null, permissions: (Permission | null)[]) =>
        (file && user && handleFilePermissions(file, { ...user }).some((permission) => permissions.includes(permission))) || false;

    const CustomActions = {
        Download: defineFileAction({
            id: 'download',
            button: { name: 'Download', contextMenu: true, icon: ChonkyIconName.download },
            requiresSelection: true,
            fileFilter: (file) => {
                const typedFile: CustomFileData = file!;

                return !!!typedFile.isDir;
            }
        }),
        DeleteFiles: defineFileAction({
            id: 'delete_files',
            button: { name: 'Delete files', contextMenu: true, icon: ChonkyIconName.trash },
            requiresSelection: true,
            fileFilter: (file) =>
                // If file is a folder only super admin and owner can delete. If it's a file then edit permission is good too
                customFileFilter(file, [
                    permissions.fullAccessOwner,
                    permissions.fullAccessSuperAdmin,
                    file?.isDir ? null : permissions.edit
                ])
        }),
        RenameFiles: defineFileAction({
            id: 'rename_files',
            button: { name: 'Rename', contextMenu: true, icon: ChonkyIconName.text },
            requiresSelection: true,
            fileFilter: (file) => customFileFilter(file, [permissions.fullAccessOwner, permissions.fullAccessSuperAdmin, permissions.edit])
        }),
        Details: defineFileAction({
            id: 'details',
            button: { name: 'Details', contextMenu: true, icon: ChonkyIconName.info },
            requiresSelection: true,
            fileFilter: (file) => {
                const typedFile: CustomFileData = file!;
                return !!typedFile.ownerId;
            }
        }),
        Manage: defineFileAction({
            id: 'manage',
            button: { name: 'Manage', contextMenu: true, icon: ChonkyIconName.key },
            requiresSelection: true,
            fileFilter: (file) => {
                const typedFile: CustomFileData = file!;
                return (
                    !!!typedFile.path?.startsWith('drive/orders') &&
                    !!typedFile.ownerId &&
                    customFileFilter(file, [permissions.fullAccessOwner, permissions.fullAccessSuperAdmin])
                );
            }
        }),
        UploadFolder: defineFileAction({
            id: 'upload_folder',
            button: { name: 'Upload folder', toolbar: true, icon: ChonkyIconName.folder }
        }),
        Compress: defineFileAction({
            id: 'compress',
            button: { name: 'Compress', contextMenu: true, icon: ChonkyIconName.archive }
        })
    };

    const { i18n, t } = useTranslation();

    const {
        currentIndex: lightboxCurrentIndex,
        isOpen: lightboxIsOpen,
        slides: lightboxSlides
    } = useAppSelector((state) => state.lightbox);
    const navigate = useNavigate();

    const { showExtended } = useLightbox();
    const { currentFileId: currentFileIdParams } = useParams();

    // This useEffect is responsible for adding url paths when going through files in lightbox. It uses ref to ignore first iteration and avoid removing path when it's initial load.
    const isFirstNavigateIteration = useRef(true);
    useEffect(() => {
        if (pathingDisabled) return;

        if (isFirstNavigateIteration.current) {
            isFirstNavigateIteration.current = false;
            return;
        }

        if (lightboxIsOpen) {
            if (lightboxSlides.length === 1) {
                if (lightboxSlides[0].id) {
                    navigate(`/drive/${currentFolderId}/${lightboxSlides[0].id}`, { replace: true });
                }
            } else {
                if (lightboxCurrentIndex !== undefined && lightboxSlides[lightboxCurrentIndex].id) {
                    navigate(`/drive/${currentFolderId}/${lightboxSlides[lightboxCurrentIndex].id}`, { replace: true });
                }
            }
        } else {
            if (window.location.pathname.split('/').length > 3) {
                const url = window.location.pathname.substring(0, window.location.pathname.lastIndexOf('/'));
                navigate(url, { replace: true });
            }
        }
    }, [lightboxCurrentIndex, lightboxIsOpen, pathingDisabled]);

    const fileBrowserRef = useRef<null | FileBrowserHandle>(null);

    const initialFiles = useFiles(fileMap, currentFolderId);

    const [searchTerm, setSearchTerm] = useState('');

    const files = useMemo(() => {
        if (_.isEmpty(initialFiles) || isBaseFileMapFetching) return [];

        return initialFiles.filter((file) => file!.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [initialFiles, isBaseFileMapFetching, searchTerm]);

    const folderChain = useFolderChain(fileMap, currentFolderId);

    const isFirstLightboxShowIteration = useRef(true);

    // This useeffect is responsible for opening lightbox on first page load if such is included in params.
    useEffect(() => {
        if (pathingDisabled || !baseFileMap || _.isEmpty(files)) return;

        if (isFirstLightboxShowIteration.current) {
            isFirstLightboxShowIteration.current = false;
        } else {
            return;
        }

        if (baseFileMap && currentFileIdParams) {
            const currentItem = baseFileMap[currentFileIdParams];
            if (files && currentItem && !currentItem.isDir && currentItem.id) {
                const filteredFiles = files.filter((file) => !file!.isDir) as CustomFileData[];
                showExtended(
                    filteredFiles.map((file) => ({ url: file.url!, id: file.id })),
                    { initialIndex: filteredFiles.findIndex((arrayFile) => arrayFile.id == currentItem.id) }
                );
            } else {
                toast.info(t('pages.drive_page.requested_file_not_found'));
                const url = window.location.pathname.substring(0, window.location.pathname.lastIndexOf('/'));
                navigate(url, { replace: true });
            }
        }
    }, [currentFileIdParams, files, pathingDisabled, baseFileMap, isBaseFileMapFetching]);

    const clearSelection = useCallback(() => {
        if (!fileBrowserRef.current) return;
        fileBrowserRef.current.requestFileAction(ChonkyActions.ClearSelection, {});
    }, [files]);

    const { open, getInputProps, getRootProps, isDragActive } = useDropzone({
        onDrop: (acceptedFiles, rejectedFiles) => {
            if (rejectedFiles.length > 0 && rejectedFiles[0].errors[0].code === 'too-many-files') {
                return toast.error(t('notifications.custom.too_many_files', { limit: 1000 }));
            }
            uploadFiles(acceptedFiles);
        },
        useFsAccessApi: false,
        noClick: true,
        maxFiles: 1000,
        multiple: true
    });

    const [fileUploadMode, setFileUploadMode] = useState<'files' | 'folder'>('files');
    const [pendingUploadIntent, setPendingUploadIntent] = useState<null | 'files' | 'folder'>(null);

    // This triggers the actual file picker after the state update finishes
    useEffect(() => {
        if (pendingUploadIntent !== null) {
            open();
            setPendingUploadIntent(null);
        }
    }, [fileUploadMode, pendingUploadIntent]);

    const handleUploadClick = (type: 'files' | 'folder') => {
        setFileUploadMode(type);
        setPendingUploadIntent(type);
    };

    const handleFileAction = useFileActionHandler(
        setCurrentFolderId,
        deleteFiles,
        compressFiles,
        moveFiles,
        renameFiles,
        createFolder,
        uploadFiles,
        handleUploadClick,
        clearSelection,
        setSearchTerm,
        files as CustomFileData[],
        CustomActions,
        pathingDisabled
    );

    const fileActions = useMemo(
        () => [
            ChonkyActions.CreateFolder,
            ChonkyActions.UploadFiles,
            CustomActions.Download,
            CustomActions.Compress,
            CustomActions.DeleteFiles,
            CustomActions.RenameFiles,
            ChonkyActions.OpenFiles,
            CustomActions.Manage,
            CustomActions.Details,
            CustomActions.UploadFolder
        ],
        []
    );

    const disableDefaultFileActions = useMemo(
        () => [process.env.NODE_ENV === 'development' && isUserSuperAdmin(user!.role) ? '' : ChonkyActions.ToggleHiddenFiles.id],
        []
    );

    const chonkyI18n: I18nConfig = {
        locale: i18n.language,
        formatters: {
            formatFileModDate: (intl: any, file: FileData | null) => {
                const safeModDate = FileHelper.getModDate(file);
                if (safeModDate) {
                    return `${moment(safeModDate).format()}, ${intl.formatTime(safeModDate)}`;
                } else {
                    return null;
                }
            }
        },
        messages: {
            'chonky.toolbar.searchPlaceholder': t('chonky.toolbar.searchPlaceholder'),
            'chonky.toolbar.visibleFileCount': t('chonky.toolbar.visibleFileCount', { fileCount: '{fileCount}' }), // Use translation key with placeholders
            'chonky.toolbar.selectedFileCount': t('chonky.toolbar.selectedFileCount', { fileCount: '{fileCount}' }),
            'chonky.toolbar.hiddenFileCount': t('chonky.toolbar.hiddenFileCount', { fileCount: '{fileCount}' }),
            'chonky.fileList.nothingToShow': t('chonky.fileList.nothingToShow'),
            'chonky.contextMenu.browserMenuShortcut': t('chonky.contextMenu.browserMenuShortcut', { shortcut: '{shortcut}' }),
            'chonky.actionGroups.Actions': t('chonky.actionGroups.Actions'),
            'chonky.actionGroups.Options': t('chonky.actionGroups.Options'),
            [`chonky.actions.${ChonkyActions.OpenParentFolder.id}.button.name`]: t('chonky.actions.OpenParentFolder.button.name'),
            [`chonky.actions.${ChonkyActions.CreateFolder.id}.button.name`]: t('chonky.actions.CreateFolder.button.name'),
            [`chonky.actions.${ChonkyActions.CreateFolder.id}.button.tooltip`]: t('chonky.actions.CreateFolder.button.tooltip'),
            [`chonky.actions.${ChonkyActions.UploadFiles.id}.button.name`]: t('chonky.actions.UploadFiles.button.name'),
            [`chonky.actions.${ChonkyActions.UploadFiles.id}.button.tooltip`]: t('chonky.actions.UploadFiles.button.tooltip'),
            [`chonky.actions.${ChonkyActions.DeleteFiles.id}.button.name`]: t('chonky.actions.DeleteFiles.button.name'),
            [`chonky.actions.${ChonkyActions.OpenSelection.id}.button.name`]: t('chonky.actions.OpenSelection.button.name'),
            [`chonky.actions.${ChonkyActions.SelectAllFiles.id}.button.name`]: t('chonky.actions.SelectAllFiles.button.name'),
            [`chonky.actions.${ChonkyActions.ClearSelection.id}.button.name`]: t('chonky.actions.ClearSelection.button.name'),
            [`chonky.actions.${CustomActions.RenameFiles.id}.button.name`]: t('chonky.actions.Rename.button.name'),
            [`chonky.actions.${ChonkyActions.EnableListView.id}.button.name`]: t('chonky.actions.EnableListView.button.name'),
            [`chonky.actions.${ChonkyActions.EnableGridView.id}.button.name`]: t('chonky.actions.EnableGridView.button.name'),
            [`chonky.actions.${ChonkyActions.SortFilesByName.id}.button.name`]: t('chonky.actions.SortFilesByName.button.name'),
            [`chonky.actions.${ChonkyActions.SortFilesBySize.id}.button.name`]: t('chonky.actions.SortFilesBySize.button.name'),
            [`chonky.actions.${ChonkyActions.SortFilesByDate.id}.button.name`]: t('chonky.actions.SortFilesByDate.button.name'),
            [`chonky.actions.${ChonkyActions.ToggleHiddenFiles.id}.button.name`]: t('chonky.actions.ToggleHiddenFiles.button.name'),
            [`chonky.actions.${ChonkyActions.ToggleShowFoldersFirst.id}.button.name`]: t(
                'chonky.actions.ToggleShowFoldersFirst.button.name'
            ),
            [`chonky.actions.${CustomActions.Manage.id}.button.name`]: t('chonky.actions.Manage.button.name'),
            [`chonky.actions.${CustomActions.Details.id}.button.name`]: t('chonky.actions.Details.button.name'),
            [`chonky.actions.${CustomActions.UploadFolder.id}.button.name`]: t('chonky.actions.UploadFolder.button.name'),
            [`chonky.actions.${CustomActions.Download.id}.button.name`]: t('chonky.actions.Download.button.name')
        }
    };

    const thumbnailGenerator = useCallback((file: CustomFileData) => file.thumbnailUrl || null, []);

    return (
        <Box
            {...getRootProps()}
            sx={{
                width: '100%',
                height: '100%',
                '.chonky-searchFieldContainer': { display: 'none' },
                '.chonky-fileListEmpty': { display: 'none' }
            }}
        >
            {/* @ts-ignore */}
            <input {...getInputProps()} {...(fileUploadMode === 'folder' ? { webkitdirectory: 'true' } : {})} />
            <Backdrop
                open={isDragActive}
                sx={{
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    width: '100%',
                    height: '100% - 40px',
                    transition: 'opacity 100ms ease-in-out',
                    zIndex: 100,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    pointerEvents: 'none',
                    flexDirection: 'column',
                    gap: 1,
                    mt: '40px'
                }}
            >
                <Cloud
                    sx={{
                        color: 'white',
                        transform: 'rotate(5deg)',
                        width: 100,
                        height: 100,
                        filter: 'drop-shadow(3px 5px 2px rgb(0 0 0 / 0.4))'
                    }}
                />
                <Typography variant="h2" color="white">
                    {t('pages.drive_page.upload_to_drive')}
                </Typography>
            </Backdrop>
            <FileBrowser
                instanceId="drive"
                files={files}
                folderChain={folderChain}
                // @ts-ignore
                fileActions={fileActions}
                onFileAction={handleFileAction}
                thumbnailGenerator={thumbnailGenerator}
                disableDefaultFileActions={disableDefaultFileActions}
                clearSelectionOnOutsideClick={false}
                ref={fileBrowserRef}
                i18n={chonkyI18n}
                hideToolbarInfo={true}
                defaultFileViewActionId={ChonkyActions.EnableListView.id}
                theme={{ gridFileEntry: { childrenCountSize: '0' } }}
            >
                <FileNavbar />
                <FileToolbar>
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder={t('chonky.toolbar.searchPlaceholder')}
                        sx={{
                            '.MuiInputBase-inputSizeSmall': { padding: '4px 0px !important' },
                            '.MuiInputBase-root ': { pl: 1 }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ fontSize: '1.1rem' }} />
                                </InputAdornment>
                            )
                        }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {isBaseFileMapFetching && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', ml: 2, mb: 0.5 }}>
                            <CircularProgress color="inherit" sx={{ color: 'rgba(0, 0, 0, 0.54)' }} size={15} />
                        </Box>
                    )}
                </FileToolbar>

                <FileList />
                <FileContextMenu />
            </FileBrowser>
        </Box>
    );
};

const forbiddenChars = /[\\/:*?"<>|]/;

const dialogSchema = z.object({
    name: z
        .string()
        .refine((name) => !name.startsWith('.'), {
            message: 'Name cannot start with a dot'
        })
        .refine((name) => !forbiddenChars.test(name), { message: 'File name shouldn\'t contain \\ / : * ? " < > |' })
});

type Dialog = z.infer<typeof dialogSchema>;

// A combined dialog that will be used in multiple places. it contains one input and additional parameters
export const inputDialog = NiceModal.create(
    ({
        submit,
        name,
        items,
        title,
        inputLabel,
        additionalMessage
    }: {
        name: string;
        items: CustomFileData[];
        title: TLike;
        inputLabel: TLike;
        additionalMessage: TLike;
        submit: (data: Dialog) => void;
    }) => {
        const modal = useModal();
        const { t } = useTranslation();

        const { control, handleSubmit } = useForm<Dialog>({
            resolver: zodResolver(dialogSchema),
            defaultValues: { name: name || '' }
        });

        const onSubmit: SubmitHandler<Dialog> = (data) => {
            submit(data);
            modal.remove();
        };

        return (
            <Modal open={modal.visible} onClose={modal.remove} sx={{ overflowY: 'scroll' }}>
                <ModalFormCard title={t(title)} gutter onClose={modal.remove}>
                    <Box component={'form'} onSubmit={handleSubmit(onSubmit)} sx={{ m: 1.5 }}>
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            {items && items.length > 0 && (
                                <>
                                    <Grid size={12}>
                                        <Typography variant="h5" sx={{ fontWeight: 400 }}>
                                            {t(additionalMessage)}:
                                        </Typography>
                                        {items.map((item) => (
                                            <Typography sx={{ fontWeight: 500 }}>{item.path}</Typography>
                                        ))}
                                    </Grid>

                                    <Grid size={12}>
                                        <Divider />
                                    </Grid>
                                </>
                            )}

                            <Grid size={12}>
                                <Controller
                                    control={control}
                                    name="name"
                                    render={({ field, fieldState: { error } }) => (
                                        <TextField
                                            {...field}
                                            size="small"
                                            label={t(inputLabel)}
                                            variant="outlined"
                                            fullWidth
                                            error={!!error}
                                            helperText={error?.message}
                                            autoFocus
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                        <DialogActions sx={{ p: 0, pt: 1 }}>
                            <Button variant="contained" size="small" type="submit">
                                {t('global.buttons.ok')}
                            </Button>
                        </DialogActions>
                    </Box>
                </ModalFormCard>
            </Modal>
        );
    }
);

export const fileDetailsDialog = NiceModal.create(({ file }: { file: CustomFileData }) => {
    const modal = useModal();
    const { user } = useAuth();
    const { t } = useTranslation();

    return (
        <Modal open={modal.visible} onClose={modal.remove} sx={{ overflowY: 'scroll' }}>
            <ModalFormCard title={t('pages.drive_page.file_details')} onClose={modal.remove} sx={{ width: 400 }} gutter>
                <Box sx={{ m: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="h4" sx={{ fontWeight: 400, mr: 0.5 }}>
                            {capitalizeFirstLetter(file.name)}
                        </Typography>
                        {file.isSystem && (
                            <Chip
                                icon={<LockOutlined fontSize="small" />}
                                label={t('pages.drive_page.system_item')}
                                color="error"
                                size="small"
                            />
                        )}

                        {file.isPublic && (
                            <Chip icon={<Public fontSize="small" />} label={t('pages.drive_page.public_item')} color="info" size="small" />
                        )}
                    </Stack>
                    <Divider sx={{ my: 1.5 }} />
                    <Grid container spacing={1}>
                        <Grid size={12} sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                            <Typography>{t('pages.drive_page.item_id')}:</Typography>
                            <Typography sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} component={'span'}>
                                {file.id}
                            </Typography>
                        </Grid>
                        <Grid size={12} sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                            <Typography>{t('pages.drive_page.location')}:</Typography>
                            <Typography sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} component={'span'}>
                                {capitalizeFirstLetter(file.path!)}
                            </Typography>
                        </Grid>
                        {!file.isDir && (
                            <Grid size={12} sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                                <Typography>{t('pages.drive_page.size')}:</Typography>
                                <Typography sx={{ whiteSpace: 'pre-wrap' }} component={'span'}>
                                    {formatBytes(file.size!)}
                                </Typography>
                            </Grid>
                        )}
                        <Grid size={12} sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                            <Typography>{t('pages.drive_page.file_type')}:</Typography>
                            <Typography component={'span'}>{file.isDir ? 'Directory' : file.ext}</Typography>
                        </Grid>
                        <Grid size={12} sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                            <Typography>{t('pages.drive_page.last_modification_date')}:</Typography>
                            <Typography sx={{ whiteSpace: 'pre-wrap' }} component={'span'}>
                                {moment(file.modDate).format()}
                            </Typography>
                        </Grid>
                        <Grid size={12} sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                            <Typography>{t('pages.drive_page.creation_date')}:</Typography>
                            <Typography sx={{ whiteSpace: 'pre-wrap' }} component={'span'}>
                                {moment(file.createDate).format()}
                            </Typography>
                        </Grid>

                        <Grid size={12} sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                            <Divider sx={{ my: 1.5, width: '100%' }} />
                        </Grid>
                        {!file.isSystem && (
                            <Grid size={12} sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                                <Typography>{t('pages.drive_page.owner')}:</Typography>
                                <Typography sx={{ whiteSpace: 'pre-wrap' }} component={'span'}>
                                    {file.ownerName} (UID:{file.ownerId})
                                </Typography>
                            </Grid>
                        )}
                        <Grid size={12} sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                            <Typography>{t('pages.drive_page.your_permissions')}:</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {user &&
                                    handleFilePermissions(file, { ...user }).map((permission) => (
                                        <Chip
                                            icon={<permission.icon fontSize="small" />}
                                            // @ts-ignore
                                            label={t(`pages.drive_page.permissions.${permission.id}`)}
                                            sx={(theme) => ({
                                                bgcolor: permission.darker ? theme.palette.grey[300] : '',
                                                p: 0.5
                                            })}
                                            size="small"
                                        />
                                    ))}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </ModalFormCard>
        </Modal>
    );
});

// Define the permission type for new additions
type PermissionType = 'read' | 'readAndEdit';

const fileManageDialogSchema = z.object({
    fileName: z.string(),
    // userId is no longer nullable as we'll use selectedUsers for multiple
    // We'll add a new field for selected users from the autocomplete
    selectedUsers: z.array(z.number()).default([]), // To hold the IDs of selected users from the autocomplete
    readPermissions: z.array(z.number()),
    editPermissions: z.array(z.number()),
    isPublic: z.boolean(),
    saveRecursively: z.boolean()
});

type FileManageDialog = z.infer<typeof fileManageDialogSchema>;

export const fileManageDialog = NiceModal.create(({ file }: { file: CustomFileData }) => {
    const modal = useModal();
    const { user } = useAuth();
    const { t } = useTranslation();

    const { data: users, ...usersData } = useFetchUsersQuery({ userId: user!.id });
    const [manageDriveItems] = useManageDriveItemsMutation();

    const { watch, control, setValue, getValues } = useForm<Nullable<FileManageDialog>>({
        defaultValues: {
            fileName: file.name,
            isPublic: file.isPublic,
            readPermissions: file.readPermissions,
            editPermissions: file.editPermissions,
            saveRecursively: true,
            selectedUsers: [] // Initialize as an empty array for multiple selection
        },
        resolver: zodResolver(fileManageDialogSchema),
        mode: 'all'
    });

    const superAdminsIds: number[] = users ? users.users.filter((user) => user.role === 'Super Admin').map((user) => user.id) : [];

    const uniqueUsersIds = [...new Set([...(watch().editPermissions || []), ...(watch().readPermissions || []), ...superAdminsIds])].sort();
    // Add file owner id && your id.
    const displayedUniqueUsersIds = [
        ...new Set([...uniqueUsersIds, ...(user!.id === file.ownerId ? [user!.id] : [file.ownerId!, user!.id])])
    ].sort();

    // State to manage the permission type for newly added users
    const [permissionType, setPermissionType] = useState<PermissionType>('read');

    // Function to handle adding selected users with the chosen permission type
    const handleAddUsers = () => {
        const currentSelectedUsers = getValues('selectedUsers') || [];
        const currentReadPermissions = new Set(getValues('readPermissions'));
        const currentEditPermissions = new Set(getValues('editPermissions'));

        currentSelectedUsers.forEach((userId) => {
            currentReadPermissions.add(userId); // Always add read permission
            if (permissionType === 'readAndEdit') {
                currentEditPermissions.add(userId); // Add edit permission if selected
            }
        });

        setValue('readPermissions', Array.from(currentReadPermissions));
        setValue('editPermissions', Array.from(currentEditPermissions));
        setValue('selectedUsers', []); // Clear selected users after adding
    };

    return (
        <Modal open={modal.visible} onClose={modal.remove} sx={{ overflowY: 'scroll' }}>
            <ModalFormCard title={t('pages.drive_page.manage_file')} onClose={modal.remove} sx={{ width: 500 }} gutter>
                <Box sx={{ m: 2 }}>
                    <Controller
                        control={control}
                        name="fileName"
                        render={({ field, fieldState: { error } }) => (
                            <TextField
                                {...field}
                                size="small"
                                fullWidth
                                label={t('pages.drive_page.file_name')}
                                value={file.name}
                                error={!!error}
                                helperText={error?.message}
                                disabled // File name should probably not be editable here
                            />
                        )}
                    />
                    <Divider sx={{ my: 1.5 }} />
                    <Grid container spacing={1}>
                        <Grid size={12}>
                            <Typography variant="h4" sx={{ fontWeight: 400, mr: 0.5 }}>
                                {t('pages.drive_page.options')}
                            </Typography>
                        </Grid>
                        <Grid size={12} sx={{ display: 'flex' }}>
                            <Controller
                                control={control}
                                name="isPublic"
                                render={({ field }) => (
                                    <FormControlLabel
                                        sx={{ mr: 0 }}
                                        control={<Checkbox {...field} checked={field.value || false} />}
                                        label={t('pages.drive_page.public_access')}
                                    />
                                )}
                            />
                            <Tooltip title={t('pages.drive_page.public_item_allows')}>
                                <Info
                                    sx={{
                                        width: 14,
                                        height: 14,
                                        cursor: 'pointer',
                                        ml: 1,
                                        my: 'auto',
                                        '&:hover': {
                                            transform: 'scale(1.2)'
                                        }
                                    }}
                                />
                            </Tooltip>
                        </Grid>
                    </Grid>
                    <Divider sx={{ my: 1.5 }} />
                    <Grid container spacing={1}>
                        <Grid size={12} sx={{ display: 'flex', gap: 1, flexDirection: 'column', mb: 1 }}>
                            <Typography variant="h4" sx={{ fontWeight: 400, mr: 0.5 }}>
                                {t('pages.drive_page.permissions.title')}:
                            </Typography>
                            {!loadCheck(usersData) ? (
                                <Controller
                                    control={control}
                                    name="selectedUsers" // Changed from userId to selectedUsers
                                    render={({ field, fieldState: { error } }) => (
                                        <Grid container spacing={1} alignItems="start">
                                            <Grid size={7}>
                                                {/* Adjust grid size for Autocomplete */}
                                                <Autocomplete
                                                    {...field}
                                                    multiple // Enable multiple selection
                                                    options={users!.users.filter(
                                                        (filterUser) =>
                                                            !uniqueUsersIds.includes(filterUser.id) &&
                                                            filterUser.id !== user!.id &&
                                                            !isUserSuperAdmin(slugify(filterUser.role, { delimiter: '_' }))
                                                    )}
                                                    value={
                                                        // Filter out users already in permissions or the current user
                                                        (field.value || [])
                                                            .map((userId) => users!.users.find((u) => u.id === userId))
                                                            .filter(Boolean) // Filter out any undefined values
                                                    }
                                                    isOptionEqualToValue={(option, value) => option!.id === value!.id}
                                                    onChange={(_, newValue) => field.onChange(newValue.map((user) => user!.id))}
                                                    getOptionLabel={(option) => option!.real_name}
                                                    size="small"
                                                    renderTags={(value, getTagProps) => (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexWrap: 'wrap',
                                                                maxWidth: '70%',
                                                                height: '100%',
                                                                overflow: 'auto',
                                                                my: 'auto',
                                                                gap: '2px'
                                                            }}
                                                        >
                                                            {[...value].reverse().map((option, index: number) => (
                                                                <Chip
                                                                    sx={{ my: '0px !important' }}
                                                                    variant="outlined"
                                                                    label={option?.real_name}
                                                                    size="small"
                                                                    {...getTagProps({ index: value.length - 1 - index })}
                                                                />
                                                            ))}
                                                        </Box>
                                                    )}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            size="small"
                                                            fullWidth
                                                            label={t('pages.drive_page.add_users')}
                                                            error={!!error}
                                                            helperText={error?.message}
                                                        />
                                                    )}
                                                />
                                            </Grid>

                                            <Grid size={5}>
                                                {/* New Grid item for permission type select */}
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>{t('pages.drive_page.permission')}</InputLabel>
                                                    <Select
                                                        value={permissionType}
                                                        label={t('pages.drive_page.permission')}
                                                        onChange={(e) => setPermissionType(e.target.value as PermissionType)}
                                                        sx={{ '.MuiSelect-select': { pt: '8px !important', pb: '9px !important' } }}
                                                    >
                                                        <MenuItem value="read">{t('pages.drive_page.permissions.read')}</MenuItem>
                                                        <MenuItem value="readAndEdit">
                                                            {t('pages.drive_page.permissions.read_and_edit')}
                                                        </MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            {/* <Grid size={2}>
                                                <Button
                                                    variant="contained"
                                                    type="button"
                                                    size="small"
                                                    disabled={getValues('selectedUsers')!.length === 0}
                                                    onClick={handleAddUsers}
                                                    fullWidth
                                                    sx={{ height: '100%' }}
                                                >
                                                    {t('global.buttons.add')}
                                                </Button>
                                            </Grid> */}
                                        </Grid>
                                    )}
                                />
                            ) : (
                                <Skeleton height={50} />
                            )}
                        </Grid>
                        {!loadCheck(usersData) &&
                            displayedUniqueUsersIds.map((uniqueUserId) => {
                                const uniqueUser = users?.users.find((user) => user.id === uniqueUserId);
                                if (!uniqueUser) return null; // Handle case where user might not be found
                                const uniqueUserPermissions = handleFilePermissions(
                                    {
                                        ...file,
                                        editPermissions: watch('editPermissions') || undefined,
                                        readPermissions: watch('readPermissions') || undefined
                                    },
                                    { id: uniqueUserId, role: slugify(uniqueUser?.role!, { delimiter: '_' }) as RoleId }
                                );
                                return (
                                    <Grid key={uniqueUserId} size={12} sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                                        <Typography>{uniqueUser?.real_name}</Typography>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            {uniqueUserPermissions.map((permission) => (
                                                <Chip
                                                    key={`${uniqueUserId}-${permission.name}`} // Add unique key
                                                    icon={<permission.icon fontSize="small" />}
                                                    // @ts-ignore
                                                    label={t(`pages.drive_page.permissions.${permission.id}`)}
                                                    sx={(theme) => ({
                                                        bgcolor: permission.darker ? theme.palette.grey[300] : '',
                                                        p: 0.5
                                                    })}
                                                    size="small"
                                                />
                                            ))}
                                            {/* Don't display permission settings if it's full access of any kind */}
                                            {!!uniqueUserPermissions.some(
                                                (uniquePermission) =>
                                                    uniquePermission !== permissions.fullAccessOwner &&
                                                    uniquePermission !== permissions.fullAccessSuperAdmin
                                            ) && (
                                                <Tooltip
                                                    title={
                                                        <List
                                                            dense
                                                            disablePadding
                                                            component={Paper}
                                                            elevation={6}
                                                            sx={(theme) => ({
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                py: 1,
                                                                '.MuiListItemButton-root': {
                                                                    '&:hover': {
                                                                        bgcolor: 'inherit'
                                                                    },
                                                                    '&.Mui-selected': {
                                                                        bgcolor: `${theme.palette.primary.light} `,
                                                                        '&:hover': {
                                                                            bgcolor: theme.palette.primary.light
                                                                        }
                                                                    }
                                                                }
                                                            })}
                                                        >
                                                            <ListItem disablePadding>
                                                                <ListItemButton
                                                                    selected={uniqueUserPermissions.includes(permissions.read)}
                                                                    sx={{
                                                                        py: 0,
                                                                        px: 1.5
                                                                    }}
                                                                    disableRipple
                                                                    onClick={() => {
                                                                        if (uniqueUserPermissions.includes(permissions.read)) {
                                                                            // If "edit" permission exists, remove it too if read is removed
                                                                            if (uniqueUserPermissions.includes(permissions.edit)) {
                                                                                setValue(
                                                                                    'editPermissions',
                                                                                    getValues('editPermissions')!.filter(
                                                                                        (id) => id !== uniqueUserId
                                                                                    )
                                                                                );
                                                                            }
                                                                            setValue(
                                                                                'readPermissions',
                                                                                getValues('readPermissions')!.filter(
                                                                                    (id) => id !== uniqueUserId
                                                                                )
                                                                            );
                                                                        } else {
                                                                            setValue('readPermissions', [
                                                                                ...getValues('readPermissions')!,
                                                                                uniqueUserId
                                                                            ]);
                                                                        }
                                                                    }}
                                                                >
                                                                    <ListItemIcon>
                                                                        <LibraryBooksOutlined fontSize="small" sx={{ color: 'black' }} />
                                                                    </ListItemIcon>
                                                                    <ListItemText primary={t('pages.drive_page.permissions.read')} />
                                                                </ListItemButton>
                                                            </ListItem>

                                                            <ListItem disablePadding>
                                                                <ListItemButton
                                                                    selected={uniqueUserPermissions.includes(permissions.edit)}
                                                                    sx={{
                                                                        py: 0,
                                                                        px: 1.5
                                                                    }}
                                                                    disableRipple
                                                                    onClick={() => {
                                                                        if (uniqueUserPermissions.includes(permissions.edit)) {
                                                                            setValue(
                                                                                'editPermissions',
                                                                                getValues('editPermissions')!.filter(
                                                                                    (id) => id !== uniqueUserId
                                                                                )
                                                                            );
                                                                        } else {
                                                                            // If adding edit permission, ensure read permission is also present
                                                                            const currentRead = getValues('readPermissions') || [];
                                                                            if (!currentRead.includes(uniqueUserId)) {
                                                                                setValue('readPermissions', [...currentRead, uniqueUserId]);
                                                                            }
                                                                            setValue('editPermissions', [
                                                                                ...getValues('editPermissions')!,
                                                                                uniqueUserId
                                                                            ]);
                                                                        }
                                                                    }}
                                                                >
                                                                    <ListItemIcon>
                                                                        <EditOutlined fontSize="small" sx={{ color: 'black' }} />
                                                                    </ListItemIcon>
                                                                    <ListItemText primary={t('pages.drive_page.permissions.edit')} />
                                                                </ListItemButton>
                                                            </ListItem>
                                                        </List>
                                                    }
                                                    PopperProps={{
                                                        placement: 'right-start',
                                                        sx: {
                                                            '.MuiTooltip-tooltip': {
                                                                p: 0,
                                                                borderRadius: 2
                                                            }
                                                        }
                                                    }}
                                                    sx={{
                                                        borderRadius: 2,
                                                        overflow: 'hidden'
                                                    }}
                                                    leaveDelay={200}
                                                >
                                                    <Chip
                                                        icon={<SettingsOutlined fontSize="small" />}
                                                        sx={{
                                                            bgcolor: 'lightgray',
                                                            p: 0.5,
                                                            '.MuiChip-label': { display: 'none' },
                                                            '.MuiSvgIcon-root': { mr: 0.5 },
                                                            cursor: 'pointer'
                                                        }}
                                                        size="small"
                                                    />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Grid>
                                );
                            })}
                    </Grid>
                    <Divider sx={{ mt: 1.5, mb: 1 }} />
                    <DialogActions sx={{ px: 0 }}>
                        {/* <Paper elevation={4} sx={{ display: 'flex', px: 1 }}>
                            <Controller
                                control={control}
                                name="saveRecursively"
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={<Checkbox {...field} checked={field.value || false} />}
                                        sx={{ mr: 0 }}
                                        label={t('pages.drive_page.recursively')}
                                    />
                                )}
                            />
                            <Tooltip title={t('pages.drive_page.saving_recursively')}>
                                <Info
                                    sx={{
                                        width: 16,
                                        height: 16,
                                        cursor: 'pointer',
                                        ml: 1,
                                        my: 'auto',
                                        '&:hover': {
                                            transform: 'scale(1.2)'
                                        }
                                    }}
                                />
                            </Tooltip>
                        </Paper> */}

                        <Button
                            variant="contained"
                            size="small"
                            color="primary"
                            onClick={() => {
                                handleAddUsers();
                                toast.promise(
                                    manageDriveItems({
                                        userId: user!.id,
                                        fileId: file.id,
                                        isPublic: getValues('isPublic') || false,
                                        saveRecursively: getValues('saveRecursively') || false,
                                        readPermissions: getValues('readPermissions') || [],
                                        editPermissions: getValues('editPermissions') || []
                                    }),
                                    {
                                        pending: t('notifications.pending.updatePending'),
                                        success: t('notifications.success.updated')
                                    }
                                );
                                modal.remove();
                            }}
                        >
                            {t('global.buttons.save')}
                        </Button>
                    </DialogActions>
                </Box>
            </ModalFormCard>
        </Modal>
    );
});

export default Drive;
